import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  addExecutionSubGroupFromExecutionSchema,
  addRunInstanceFromExecutionSchema,
  executionNameSchema,
} from "../../../schemas";
import CustomAccordion from "../Accordian/CustomAccordion";
import useFetchRunInstance from "../../../hooks/useFetchRunInstance";
import useFetchExecutionSubGroup from "../../../hooks/useFetchExecutionSubGroup";
import {
  IconExistingRunName,
  IconNewRunName,
} from "../../../common/utils/icons";
import { addRunInstance } from "../../../services/runInstance";
import { addExecutionSubGroup } from "../../../services/executionSubGroup";
import { resolveExecutionSubGroupOption } from "../../../utils/executionGroup";
import { useToast } from "../../../services/utils";

interface ExecutionNameProps {
  handleChangeAccordion?: any;
  errors?: any;
  setErrors?: any;
  executionName?: any;
  setExecutionName?: any;
  executionMode?: boolean;
  setExecutionMode?: any;
  isShowExecutionMode?: boolean;
  // Standard run parameter controls (relocated from RunParameters)
  runParameters?: any;
  setRunParameters?: Dispatch<SetStateAction<any>>;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
  isReRun?: boolean;
  isShowLongRunningJob?: boolean;
  isShowExecutionGroup?: boolean;
  isShowExecutionSubGroup?: boolean;
}

const noopSetLoading: Dispatch<SetStateAction<boolean>> = () => {};

const ExecutionName = ({
  handleChangeAccordion,
  errors,
  setErrors,
  executionName,
  setExecutionName,
  executionMode,
  setExecutionMode,
  isShowExecutionMode = true,
  runParameters,
  setRunParameters,
  setIsLoading,
  isReRun = false,
  isShowLongRunningJob = true,
  isShowExecutionGroup = true,
  isShowExecutionSubGroup = true,
}: ExecutionNameProps) => {
  const hasStandardParams = typeof setRunParameters === "function";
  const { showToast } = useToast();
  const [runInstanceData] = useFetchRunInstance({
    setIsLoading: setIsLoading || noopSetLoading,
    fetchOnMount: hasStandardParams && isShowExecutionGroup,
  });
  const [executionSubGroupData] = useFetchExecutionSubGroup({
    setIsLoading: setIsLoading || noopSetLoading,
    fetchOnMount: hasStandardParams && isShowExecutionSubGroup,
  });

  const [localRunInstanceData, setLocalRunInstanceData] = useState<any[]>([]);
  const [localSubGroupData, setLocalSubGroupData] = useState<any[]>([]);
  useEffect(() => {
    setLocalRunInstanceData(runInstanceData || []);
  }, [runInstanceData]);
  useEffect(() => {
    setLocalSubGroupData(executionSubGroupData || []);
  }, [executionSubGroupData]);

  // DQ-8820: keep the selected sub group visible even when it is absent from
  // the active list (or the list has not loaded yet), otherwise a re-run —
  // where the field is read-only — renders empty and cannot be submitted.
  const selectedSubGroupOption = useMemo(
    () =>
      resolveExecutionSubGroupOption(
        localSubGroupData,
        runParameters?.execution_sub_group,
      ),
    [localSubGroupData, runParameters?.execution_sub_group],
  );
  const subGroupOptions = useMemo(() => {
    if (
      !selectedSubGroupOption ||
      localSubGroupData.some(
        (option: any) =>
          option?.id != null &&
          String(option.id) === String(selectedSubGroupOption.id),
      )
    ) {
      return localSubGroupData;
    }
    return [selectedSubGroupOption, ...localSubGroupData];
  }, [localSubGroupData, selectedSubGroupOption]);

  const [isOldRunName, setIsOldRunName] = useState<boolean>(true);
  const [newRunName, setNewRunName] = useState<string>("");
  const newRunNameRef = useRef<HTMLInputElement | null>(null);
  const [runNameErrors, setRunNameErrors] = useState<{ [key: string]: string }>(
    {}
  );

  const [isOldSubGroup, setIsOldSubGroup] = useState<boolean>(true);
  const [newSubGroupCode, setNewSubGroupCode] = useState<string>("");
  const newSubGroupCodeRef = useRef<HTMLInputElement | null>(null);
  const [subGroupErrors, setSubGroupErrors] = useState<{
    [key: string]: string;
  }>({});

  const handleExeuctionName = (e: any) => {
    setExecutionName(e.target.value);
    validateField(e.target.name, e.target.value);
  };

  const handleExecutionModeChange = (e: any) => {
    setExecutionMode && setExecutionMode(e.target.checked);
  };

  const handleLongRunningJobChange = (e: any) => {
    setRunParameters &&
      setRunParameters((prev: any) => ({
        ...prev,
        is_long_running_job: e.target.checked,
      }));
  };

  const handleChangeRunInstance = (_event: any, value: any) => {
    setRunParameters &&
      setRunParameters((prev: any) => ({
        ...prev,
        run_instance: {
          run_id: value?.id ?? null,
          run_name: value?.run_name ?? null,
        },
      }));
  };

  const handleChangeExecutionSubGroup = (_event: any, value: any) => {
    setRunParameters &&
      setRunParameters((prev: any) => ({
        ...prev,
        execution_sub_group: {
          execution_sub_group_id: value?.id ?? null,
          execution_sub_group_code:
            value?.execution_sub_group_code || value?.code || null,
        },
      }));
  };

  const validateField = async (name: any, value: any) => {
    try {
      const partialFormData = { executionName, [name]: value };
      await executionNameSchema.validateAt(name, partialFormData);
      setErrors((prevErrors: any) => ({ ...prevErrors, [name]: "" }));
    } catch (validationError: any) {
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  const handleFocusRunNameField = () => {
    setTimeout(() => {
      if (newRunNameRef.current) {
        newRunNameRef.current.focus();
      }
    }, 50);
  };

  const handleFocusSubGroupField = () => {
    setTimeout(() => {
      if (newSubGroupCodeRef.current) {
        newSubGroupCodeRef.current.focus();
      }
    }, 50);
  };

  const handleSaveRunName = async (e: any) => {
    e.preventDefault();
    const reqBody = { run_name: newRunName, code: newRunName };
    try {
      await addRunInstanceFromExecutionSchema.validate(reqBody, {
        abortEarly: false,
      });
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach((error: any) => {
        newErrors[error.path] = error.message;
      });
      setRunNameErrors(newErrors);
      return;
    }
    try {
      const response = await addRunInstance({ payload: reqBody });
      if (response && response.id) {
        setLocalRunInstanceData((prev) => [response, ...prev]);
        setRunParameters &&
          setRunParameters((prev: any) => ({
            ...prev,
            run_instance: {
              run_id: response.id,
              run_name: response.run_name,
            },
          }));
        setNewRunName("");
        setRunNameErrors({});
        setIsOldRunName(true);
        showToast("Execution Group created successfully!", "success");
      }
    } catch (error: any) {
      showToast(error?.message || "Failed to create execution group", "error");
    }
  };

  const handleSaveSubGroupCode = async (e: any) => {
    e.preventDefault();
    const reqBody = {
      execution_sub_group_code: newSubGroupCode,
      code: newSubGroupCode,
      name: newSubGroupCode,
    };
    try {
      await addExecutionSubGroupFromExecutionSchema.validate(reqBody, {
        abortEarly: false,
      });
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach((error: any) => {
        newErrors[error.path] = error.message;
      });
      setSubGroupErrors(newErrors);
      return;
    }
    try {
      const response = await addExecutionSubGroup({ payload: reqBody });
      if (response && response.id) {
        setLocalSubGroupData((prev) => [response, ...prev]);
        setRunParameters &&
          setRunParameters((prev: any) => ({
            ...prev,
            execution_sub_group: {
              execution_sub_group_id: response.id,
              execution_sub_group_code:
                response.execution_sub_group_code || response.code || "",
            },
          }));
        setNewSubGroupCode("");
        setSubGroupErrors({});
        setIsOldSubGroup(true);
        showToast("Execution Sub Group created successfully!", "success");
      }
    } catch (error: any) {
      showToast(
        error?.message || "Failed to create execution sub group",
        "error"
      );
    }
  };

  return (
    <CustomAccordion
      expandId="panel2d-header-ExecutionName"
      title={"Execution Name"}
      isEnabled={true}
      isDefaultExpaned={true}
    >
      <Grid container spacing={4}>
        <Grid item xs={12} sm={12} md={4} lg={4}>
          <TextField
            type="text"
            fullWidth
            variant="outlined"
            placeholder="Enter execution name"
            className={`form-control-autocomplete ${
              errors?.execution_name ? "has-error" : ""
            }`}
            value={executionName}
            name="execution_name"
            onChange={handleExeuctionName}
            error={!!errors?.execution_name}
            helperText={errors?.execution_name || ""}
            inputProps={{
              style: { textOverflow: "ellipsis" },
            }}
          />
        </Grid>
        {isShowExecutionMode && (
          <Grid item xs={12} sm={12} md={4} lg={4}>
            <FormControl component="fieldset" className="switch-controller">
              <FormControlLabel
                control={
                  <Switch
                    checked={executionMode}
                    onChange={handleExecutionModeChange}
                    name="executionMode"
                    color="primary"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "var(--orange)",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "var(--orange)",
                        },
                    }}
                  />
                }
                label={
                  <Typography>
                    {executionMode
                      ? "Execution in Background"
                      : "Execution in Normal"}
                  </Typography>
                }
              />
            </FormControl>
          </Grid>
        )}
        {hasStandardParams && isShowLongRunningJob && (
          <Grid item xs={12} sm={12} md={4} lg={4}>
            <FormControl component="fieldset" className="switch-controller">
              <FormControlLabel
                control={
                  <Switch
                    checked={!!runParameters?.is_long_running_job}
                    onChange={handleLongRunningJobChange}
                    name="isLongRunningJob"
                    color="primary"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "var(--orange)",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "var(--orange)",
                        },
                    }}
                  />
                }
                label={
                  <Typography>
                    Long Running Job{" "}
                    <Tooltip
                      title="When enabled, the execution is routed to the long-running job worker for better handling of large data sets."
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 16,
                          color: "grey.500",
                          ml: 0.5,
                          verticalAlign: "middle",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  </Typography>
                }
              />
            </FormControl>
          </Grid>
        )}
      </Grid>
      {hasStandardParams &&
        (isShowExecutionGroup || isShowExecutionSubGroup) && (
          <Grid container spacing={4} sx={{ marginTop: "16px" }}>
            {isShowExecutionGroup && (
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Box className="label-text line-height16">Execution Group</Box>
                <Box className="run-name-update">
                  <Box sx={{ flex: "1" }}>
                    {isOldRunName ? (
                      <Autocomplete
                        fullWidth
                        options={localRunInstanceData}
                        getOptionLabel={(option: any) => option?.run_name || ""}
                        isOptionEqualToValue={(option: any, value: any) =>
                          option?.id === value?.id
                        }
                        onChange={handleChangeRunInstance}
                        value={
                          localRunInstanceData.find(
                            (option: any) =>
                              option?.id === runParameters?.run_instance?.run_id
                          ) || null
                        }
                        renderInput={(params) => (
                          <TextField
                            name="run_instance"
                            {...params}
                            placeholder="Select..."
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                        loadingText="Loading..."
                        className="form-control-autocomplete"
                        disabled={isReRun}
                      />
                    ) : (
                      <TextField
                        type="text"
                        fullWidth
                        variant="outlined"
                        placeholder="Enter new name"
                        className="form-control-autocomplete"
                        onChange={(e) => {
                          setNewRunName(e.target.value);
                          if (runNameErrors.run_name) {
                            setRunNameErrors((prev) => ({
                              ...prev,
                              run_name: "",
                            }));
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        value={newRunName || ""}
                        inputRef={newRunNameRef}
                        disabled={isReRun}
                        error={!!runNameErrors.run_name}
                        helperText={runNameErrors.run_name || ""}
                      />
                    )}
                  </Box>
                  <Box className="btn-group">
                    {isOldRunName ? (
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => {
                          setIsOldRunName(false);
                          handleFocusRunNameField();
                        }}
                        className="btn-orange btn-dark"
                        disabled={isReRun}
                      >
                        <Tooltip title="Add Execution Group" placement="top">
                          <Box>
                            <IconNewRunName />
                          </Box>
                        </Tooltip>
                      </Button>
                    ) : (
                      <>
                        <Button
                          type="submit"
                          color="secondary"
                          variant="contained"
                          className="btn-orange btn-dark"
                          onClick={handleSaveRunName}
                          disabled={isReRun}
                        >
                          <Tooltip title="Save Execution Group" placement="top">
                            <Box>
                              <SaveOutlinedIcon />
                            </Box>
                          </Tooltip>
                        </Button>
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={() => {
                            setIsOldRunName(true);
                            setRunNameErrors({});
                          }}
                          className="btn-orange btn-dark"
                          disabled={isReRun}
                        >
                          <Tooltip
                            title="Add existing Execution Group"
                            placement="top"
                          >
                            <Box>
                              <IconExistingRunName />
                            </Box>
                          </Tooltip>
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              </Grid>
            )}
            {isShowExecutionSubGroup && (
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Box className="label-text line-height16">
                  Execution Sub Group
                </Box>
                <Box className="run-name-update">
                  <Box sx={{ flex: "1" }}>
                    {isOldSubGroup ? (
                      <Autocomplete
                        fullWidth
                        options={subGroupOptions}
                        getOptionLabel={(option: any) =>
                          option?.name ||
                          option?.execution_sub_group_code ||
                          option?.code ||
                          ""
                        }
                        isOptionEqualToValue={(option: any, value: any) =>
                          String(option?.id) === String(value?.id)
                        }
                        onChange={handleChangeExecutionSubGroup}
                        value={selectedSubGroupOption}
                        renderInput={(params) => (
                          <TextField
                            name="execution_sub_group"
                            {...params}
                            placeholder="Select..."
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                        loadingText="Loading..."
                        className="form-control-autocomplete"
                        disabled={isReRun}
                      />
                    ) : (
                      <TextField
                        type="text"
                        fullWidth
                        variant="outlined"
                        placeholder="Enter new code"
                        className="form-control-autocomplete"
                        onChange={(e) => {
                          setNewSubGroupCode(e.target.value);
                          if (subGroupErrors.execution_sub_group_code) {
                            setSubGroupErrors((prev) => ({
                              ...prev,
                              execution_sub_group_code: "",
                            }));
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        value={newSubGroupCode || ""}
                        inputRef={newSubGroupCodeRef}
                        disabled={isReRun}
                        error={!!subGroupErrors.execution_sub_group_code}
                        helperText={
                          subGroupErrors.execution_sub_group_code || ""
                        }
                      />
                    )}
                  </Box>
                  <Box className="btn-group">
                    {isOldSubGroup ? (
                      <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => {
                          setIsOldSubGroup(false);
                          handleFocusSubGroupField();
                        }}
                        className="btn-orange btn-dark"
                        disabled={isReRun}
                      >
                        <Tooltip
                          title="Add Execution Sub Group"
                          placement="top"
                        >
                          <Box>
                            <IconNewRunName />
                          </Box>
                        </Tooltip>
                      </Button>
                    ) : (
                      <>
                        <Button
                          type="submit"
                          color="secondary"
                          variant="contained"
                          className="btn-orange btn-dark"
                          onClick={handleSaveSubGroupCode}
                          disabled={isReRun}
                        >
                          <Tooltip
                            title="Save Execution Sub Group"
                            placement="top"
                          >
                            <Box>
                              <SaveOutlinedIcon />
                            </Box>
                          </Tooltip>
                        </Button>
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={() => {
                            setIsOldSubGroup(true);
                            setSubGroupErrors({});
                          }}
                          className="btn-orange btn-dark"
                          disabled={isReRun}
                        >
                          <Tooltip
                            title="Add existing Execution Sub Group"
                            placement="top"
                          >
                            <Box>
                              <IconExistingRunName />
                            </Box>
                          </Tooltip>
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
    </CustomAccordion>
  );
};

export default ExecutionName;
